import { ChevronRight, Download, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'

import { adminCensus } from 'rails_routes'
import { ButtonLink, Tooltip, TooltipProvider } from 'slab'

import { BenchmarkList } from './BenchmarkList'
import { GettingQuoteFailed } from './GetErrorBanners'
import { getTypeForCensusStatus } from './ProspectCensusDetails'
import { GenerateBenchmarkForm } from './QuoteCreateBenchmark'
import { GenerateQuoteForm } from './QuoteCreateGenerateQuote'
import { QuoteErrorBanner, QuoteProcessingBanner } from './QuoteCreateResult'
import { doesBenchmarksExist, hasQuoteGenerationStarted } from './QuoteDetails'
import { type Quote } from './types'
import { getLocaleDateString, getTimezoneDatetimeStrings } from '../../utils'
import { useGetQuote } from '../hooks/useGetQuote'
import Badge from '../reusable/Badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'

function BenchmarkParameters ({ quote }: { quote: Quote }) {
  return (
    <div className='space-y-6'>
      <h2 className='h2 font-normal'>Benchmark parameters</h2>
      <GenerateBenchmarkForm
        prospectId={quote.quoting_prospect_id}
        quoteId={quote.id}
        onGoBackToCensus={() => {}}
        onSkipBenchmarkGeneration={() => {}}
        onSuccess={() => {}}
        onFailure={() => {}}
        quote={quote}
        readOnly
      />
    </div>
  )
}

function BenchmarkListForThisQuote ({ quote }: { quote: Quote }) {
  return (
    <div className='space-y-6'>
      <h2 className='h2 font-normal'>Suggested benchmarks</h2>
      <BenchmarkList
        prospectId={quote.quoting_prospect_id}
        quoteId={quote.id}
      />
    </div>
  )
}

function QuoteParameters ({ quote }: { quote: Quote }) {
  return (
    <div className='space-y-6'>
      <h2 className='h2 font-normal'>Coverage levels</h2>
      <GenerateQuoteForm
        prospectId={quote.quoting_prospect_id}
        quoteId={quote.id}
        onSuccess={() => {}}
        onFailure={() => {}}
        quote={quote}
        readOnly
        onGoBackToBenchmarkParameters={() => {}}
      />
    </div>
  )
}

function DetailBox ({ quote }: { quote: Quote }) {
  const { formattedDate, formattedTime } = getTimezoneDatetimeStrings({
    date: new Date(quote.updated_at),
    timeZone: 'EST',
  })
  return (
    <dl className='flex flex-col gap-y-4 rounded-lg border border-ds-border-primary-default p-4 text-sm text-ds-text-default my-6'>
      <div className='flex gap-x-6 items-center'>
        <dt className='font-medium w-1/3'>Name</dt>
        <dd className='flex-1'>{quote.name ?? 'n/a'}</dd>
      </div>
      <div className='flex gap-x-6 items-center'>
        <dt className='font-medium w-1/3'>Date Created</dt>
        <dd className='flex-1'>{formattedDate}</dd>
      </div>
      <div className='flex gap-x-6 items-center'>
        <dt className='font-medium w-1/3'>Time Created (EST)</dt>
        <dd className='flex-1'>{formattedTime}</dd>
      </div>
      <div className='flex gap-x-6 items-center'>
        <dt className='font-medium w-1/3'>Status</dt>
        <dd className='flex-1'>
          <Badge
            type={
              quote.status ? getTypeForCensusStatus(quote.status) : 'default'
            }
            text={quote.status ?? 'Unknown'}
          />
        </dd>
      </div>
      <div className='flex gap-x-6 items-center'>
        <dt className='font-medium w-1/3'>Coverage start date</dt>
        <dd className='flex-1'>
          {quote.coverage_start_date
            ? getLocaleDateString(new Date(quote.coverage_start_date))
            : '-'}
        </dd>
      </div>
      <div className='flex gap-x-6'>
        <dt className='font-medium w-1/3'>Census used</dt>
        <dd className='flex-1 flex flex-col'>
          {quote.census_file_name}
          <a
            className='btn-link'
            href={adminCensus.show({ prospect_id: quote.quoting_prospect_id, id: quote.quoting_prospect_employee_census_id })}
            target='_blank'
            rel='noreferrer'
          >
            See census details
            <ChevronRight size={18} />
          </a>
        </dd>
      </div>
      <div className='flex gap-x-6 items-center'>
        <dt className='font-medium w-1/3'>Allowance type</dt>
        <dd className='flex-1'>
          {quote.has_flat_allowances ? 'Flat' : 'Dynamic'}
        </dd>
      </div>
    </dl>
  )
}

function QuoteDetailsSheetImpl ({ quote, quotingV2DynamicQuoteEditorPath, quotingV2FlatQuoteEditorPath }: { quote: Quote, quotingV2DynamicQuoteEditorPath?: string, quotingV2FlatQuoteEditorPath?: string }) {
  return (
    <div className='space-y-10'>
      <div className='flex items-center gap-x-2'>
        <TooltipProvider>
          <Tooltip
            content='Only processed quotes with dynamic allowances can be downloaded'
            enabled={quote.status !== 'processed' || quote.has_flat_allowances}
            zIndex={100}
          >
            <div>
              <ButtonLink
                href={quote.download_link ?? undefined}
                variant='primary'
                disabled={quote.status !== 'processed' || quote.has_flat_allowances}
                download
              >
                <div className='flex items-center gap-x-2'>
                  <Download size={16} />
                  Download quote
                </div>
              </ButtonLink>
            </div>
          </Tooltip>
        </TooltipProvider>
        {(quotingV2DynamicQuoteEditorPath || quotingV2FlatQuoteEditorPath) && (
          <ButtonLink
            href={quote.has_flat_allowances
              ? quotingV2FlatQuoteEditorPath!.replace(':quote_id', quote.id)
              : quotingV2DynamicQuoteEditorPath!.replace(':quote_id', quote.id)}
            variant='secondary'
          >
            <div className='flex items-center gap-x-2'>
              <SlidersHorizontal size={16} />
              Quote editor
            </div>
          </ButtonLink>
        )}
      </div>
      {quote.status === 'failed' && (
        <QuoteErrorBanner quoteErrors={quote.quote_errors} />
      )}
      {quote.status === 'processing' ||
        (quote.status === 'unprocessed' && <QuoteProcessingBanner />)}
      <DetailBox quote={quote} />
      {hasQuoteGenerationStarted(quote) && <QuoteParameters quote={quote} />}
      {doesBenchmarksExist(quote) && (
        <>
          <BenchmarkParameters quote={quote} />
          <BenchmarkListForThisQuote quote={quote} />
        </>
      )}
    </div>
  )
}

export function QuoteDetailsSheet ({
  prospectId,
  quoteId,
  onClose,
  quotingV2DynamicQuoteEditorPath,
  quotingV2FlatQuoteEditorPath,
}: {
  prospectId: string;
  quoteId: string;
  onClose: () => void;
  quotingV2DynamicQuoteEditorPath?: string;
  quotingV2FlatQuoteEditorPath?: string;
}) {
  const [open, setOpen] = useState(!!quoteId)
  const { data: quote, error, isLoading } = useGetQuote({ prospectId, quoteId })
  // TODO: improve the loading state and empty census state

  const handleOpenChange = () => {
    setOpen(!open)
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        className='sm:max-w-3xl overflow-auto'
        onAnimationEndCapture={() => {
          if (!open) {
            // We want to handle onclose here so that we can destroy this component after the animation has ended
            onClose()
          }
        }}
        aria-describedby={undefined}
      >
        <SheetHeader>
          <SheetTitle className='pb-10'>Quote Details</SheetTitle>
        </SheetHeader>
        {error && !isLoading && !quote && <GettingQuoteFailed errorObj={error} />}
        {quote && !isLoading && !error &&
          <QuoteDetailsSheetImpl
            quote={quote}
            quotingV2DynamicQuoteEditorPath={quotingV2DynamicQuoteEditorPath}
            quotingV2FlatQuoteEditorPath={quotingV2FlatQuoteEditorPath}
          />}
      </SheetContent>
    </Sheet>
  )
}
